import apiClient from './index';

export interface SmtpSettings {
  enabled: boolean;
  host: string;
  port: number;
  username: string;
  password: string;
  use_ssl: boolean;
  use_tls: boolean;
  from_email: string;
  success_to_email: string;
  success_cc_email: string;
  failure_to_email: string;
  failure_cc_email: string;
}

export interface EtlSettings {
  client_mode: 'DELTA' | 'FULL';
  client_locale: string;
  cleanup_on_success: boolean;
  connection_retries: number;
  ftp_host: string;
  ftp_username: string;
  ftp_password: string;
  ftp_directory: string;
}

export interface AccountSettings {
  username: string;
  has_custom_password: boolean;
}

export interface AppSettings {
  smtp: SmtpSettings;
  etl: EtlSettings;
  account: AccountSettings;
}

export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
  new_username?: string;
}

export interface PasswordChangeResponse {
  status: 'success' | 'error';
  message: string;
  username?: string;
  error?: string;
}

export interface SettingsUpdateResponse {
  status: 'success' | 'error';
  message: string;
  settings?: AppSettings;
  error?: string;
}

export async function getSettings(): Promise<AppSettings> {
  const response = await apiClient.get<AppSettings>('/spex/plus/settings');
  return response.data;
}

export async function updateSmtpSettings(
  smtp: Partial<SmtpSettings>
): Promise<SettingsUpdateResponse> {
  const response = await apiClient.put<SettingsUpdateResponse>(
    '/spex/plus/settings/smtp',
    smtp
  );
  return response.data;
}

export async function updateEtlSettings(
  etl: Partial<EtlSettings>
): Promise<SettingsUpdateResponse> {
  const response = await apiClient.put<SettingsUpdateResponse>(
    '/spex/plus/settings/etl',
    etl
  );
  return response.data;
}

export async function changePassword(
  data: PasswordChangeRequest
): Promise<PasswordChangeResponse> {
  const response = await apiClient.put<PasswordChangeResponse>(
    '/spex/plus/settings/account/password',
    data
  );
  return response.data;
}

// Alias types and API functions
export interface Alias {
  id: number;
  alias_name: string;
  canonical_name: string;
  created_at: string;
}

export interface AliasCreateRequest {
  alias_name: string;
  canonical_name: string;
}

export interface AliasResponse {
  status: 'success' | 'error';
  message: string;
  error?: string;
}

export async function getManufacturerAliases(): Promise<Alias[]> {
  const response = await apiClient.get<Alias[]>('/spex/plus/aliases/manufacturers');
  return response.data;
}

export async function addManufacturerAlias(data: AliasCreateRequest): Promise<AliasResponse> {
  const response = await apiClient.post<AliasResponse>('/spex/plus/aliases/manufacturers', data);
  return response.data;
}

export async function deleteManufacturerAlias(id: number): Promise<AliasResponse> {
  const response = await apiClient.delete<AliasResponse>(`/spex/plus/aliases/manufacturers/${id}`);
  return response.data;
}

export async function getCategoryAliases(): Promise<Alias[]> {
  const response = await apiClient.get<Alias[]>('/spex/plus/aliases/categories');
  return response.data;
}

export async function addCategoryAlias(data: AliasCreateRequest): Promise<AliasResponse> {
  const response = await apiClient.post<AliasResponse>('/spex/plus/aliases/categories', data);
  return response.data;
}

export async function deleteCategoryAlias(id: number): Promise<AliasResponse> {
  const response = await apiClient.delete<AliasResponse>(`/spex/plus/aliases/categories/${id}`);
  return response.data;
}
